/*
 * CompanyPage Messages
 *
 * This contains all the text for the Company Page container.
 */

import { defineMessages } from 'react-intl';
import { ETransportType } from '../../../common/types';
import {
  DESTINATIONS,
  DISCOUNT_CARD_CALCULATOR,
  FAQ,
  HISTORY,
  MANUAL_DATA,
  NEWS_FEED,
  SALES_OPENING,
  SERVICE_ACCESSIBILITY,
  SERVICE_AIR_CONDITIONING,
  SERVICE_ANIMAL,
  SERVICE_BIKE,
  SERVICE_LUGGAGE,
  SERVICE_MEAL,
  SERVICE_MEDIA,
  SERVICE_POWER,
  SERVICE_RESTROOM,
  SERVICE_WIFI,
  SERVICES,
} from '../constants';

export const companyScope = 'app.containers.Company';

export const companyMessages = {
  header: {
    id: `${companyScope}.header`,
    defaultMessage: 'Company Page',
    en: 'Company Page',
    es: 'Página de la empresa',
    it: 'Pagina aziendale',
    de: 'Unternehmensseite',
    nl: 'Bedrijfspagina',
  },
  [DESTINATIONS]: {
    id: `${companyScope}.${DESTINATIONS}`,
    defaultMessage: 'Découvrir',
    en: 'Discover',
    es: 'Descubrir',
    it: 'Scoprire',
    de: 'Entdecken',
    nl: 'Ontdekken',
  },
  [HISTORY]: {
    id: `${companyScope}.${HISTORY}`,
    defaultMessage: 'En savoir plus',
    en: 'Learn more',
    es: 'Más información',
    it: 'Maggiori informazioni',
    de: 'Mehr erfahren',
    nl: 'Meer informatie',
  },
  inBrief: {
    id: `${companyScope}.inBrief`,
    defaultMessage: 'En bref',
    en: 'In brief',
    es: 'En resumen',
    it: 'In breve',
    de: 'Kurz gesagt',
    nl: 'In het kort',
  },
  [DISCOUNT_CARD_CALCULATOR]: {
    id: `${companyScope}.${DISCOUNT_CARD_CALCULATOR}`,
    defaultMessage: 'Calculer mes économies',
    en: 'Work out my savings',
    es: 'Calcular mi ahorro',
    it: 'Calcola il mio risparmio',
    de: 'Ersparnis berechnen',
    nl: 'Mijn besparing berekenen',
  },
  [SALES_OPENING]: {
    id: `${companyScope}.${SALES_OPENING}`,
    defaultMessage: 'Ouverture des ventes',
    en: 'Sales opening',
    es: 'Apertura de ventas',
    it: 'Apertura delle vendite',
    de: 'Verkaufsstart',
    nl: 'Verkoopstart',
  },
  [SERVICES]: {
    id: `${companyScope}.${SERVICES}`,
    defaultMessage: 'Services à bord',
    en: 'Onboard Services',
    es: 'Servicios a bordo',
    it: 'Servizi a bordo',
    de: 'Dienstleistungen an Bord',
    nl: 'Diensten aan boord',
  },
  [MANUAL_DATA]: {
    id: `${companyScope}.${MANUAL_DATA}`,
    defaultMessage: 'Bon à savoir',
    en: 'Good to know',
    es: 'Información útil',
    it: 'Da sapere',
    de: 'Gut zu wissen',
    nl: 'Handig om te weten',
  },
  serviceIncluded: {
    id: `${companyScope}.serviceIncluded`,
    defaultMessage: 'Inclus',
    en: 'Included',
    es: 'Incluido',
    it: 'Incluso',
    de: 'Inbegriffen',
    nl: 'Inbegrepen',
  },
  serviceExcluded: {
    id: `${companyScope}.serviceExcluded`,
    defaultMessage: 'Non inclus',
    en: 'Not included',
    es: 'No incluido',
    it: 'Non incluso',
    de: 'Nicht inbegriffen',
    nl: 'Niet inbegrepen',
  },
  [FAQ]: {
    id: `${companyScope}.${FAQ}`,
    defaultMessage: 'FAQ de la compagnie',
    en: 'Company FAQ',
    es: 'Preguntas frecuentes de la empresa',
    it: "Domande frequenti dell'azienda",
    de: 'FAQ des Unternehmens',
    nl: 'Veelgestelde vragen van het bedrijf',
  },
  [NEWS_FEED]: {
    id: `${companyScope}.${NEWS_FEED}`,
    defaultMessage: 'Actualités de la compagnie',
    en: 'Company News Feed',
    es: 'Noticias de la empresa',
    it: "News feed dell'azienda",
    de: 'Unternehmensnachrichten',
    nl: 'Bedrijfsnieuwsfeed',
  },
  [SERVICE_POWER]: {
    id: `${companyScope}.${SERVICE_POWER}`,
    defaultMessage: 'Prises',
    en: 'Power Outlets',
    es: 'Enchufes',
    it: 'Prese di corrente',
    de: 'Steckdosen',
    nl: 'Stopcontacten',
  },
  [SERVICE_ACCESSIBILITY]: {
    id: `${companyScope}.${SERVICE_ACCESSIBILITY}`,
    defaultMessage: 'Accessibilité',
    en: 'Accessibility',
    es: 'Accesibilidad',
    it: 'Accessibilità',
    de: 'Barrierefreiheit',
    nl: 'Toegankelijkheid',
  },
  [SERVICE_AIR_CONDITIONING]: {
    id: `${companyScope}.${SERVICE_AIR_CONDITIONING}`,
    defaultMessage: 'Clim',
    en: 'Air Conditioning',
    es: 'Aire acondicionado',
    it: 'Aria condizionata',
    de: 'Klimaanlage',
    nl: 'Airconditioning',
  },
  [SERVICE_ANIMAL]: {
    id: `${companyScope}.${SERVICE_ANIMAL}`,
    defaultMessage: 'Animaux',
    en: 'Animals',
    es: 'Animales',
    it: 'Animali',
    de: 'Tiere',
    nl: 'Dieren',
  },
  [SERVICE_BIKE]: {
    id: `${companyScope}.${SERVICE_BIKE}`,
    defaultMessage: 'Vélo',
    en: 'Bike',
    es: 'Bicicleta',
    it: 'Bicicletta',
    de: 'Fahrrad',
    nl: 'Fiets',
  },
  [SERVICE_LUGGAGE]: {
    id: `${companyScope}.${SERVICE_LUGGAGE}`,
    defaultMessage: 'Bagages',
    en: 'Luggage',
    es: 'Equipaje',
    it: 'Bagagli',
    de: 'Gepäck',
    nl: 'Bagage',
  },
  [SERVICE_MEAL]: {
    id: `${companyScope}.${SERVICE_MEAL}`,
    defaultMessage: 'Restauration',
    en: 'Meal Service',
    es: 'Servicio de comidas',
    it: 'Servizio pasti',
    de: 'Verpflegung',
    nl: 'Maaltijdservice',
  },
  [SERVICE_MEDIA]: {
    id: `${companyScope}.${SERVICE_MEDIA}`,
    defaultMessage: 'Media',
    en: 'Media',
    es: 'Medios de comunicación',
    it: 'Media',
    de: 'Medien',
    nl: 'Media',
  },
  [SERVICE_WIFI]: {
    id: `${companyScope}.${SERVICE_WIFI}`,
    defaultMessage: 'Wifi',
    en: 'Wifi',
    es: 'Wifi',
    it: 'Wifi',
    de: 'Wifi',
    nl: 'Wifi',
  },
  [SERVICE_RESTROOM]: {
    id: `${companyScope}.${SERVICE_RESTROOM}`,
    defaultMessage: 'WC',
    en: 'Restroom',
    es: 'Baño',
    it: 'Bagno',
    de: 'WC',
    nl: 'Toilet',
  },
  seeAlso: {
    id: `${companyScope}.seeAlso`,
    defaultMessage: 'À voir aussi',
    en: 'See also',
    es: 'Ver también',
    it: 'Vedi anche',
    de: 'Siehe auch',
    nl: 'Zie ook',
  },
  website: {
    id: `${companyScope}.website`,
    defaultMessage: 'Site ',
    en: 'Website ',
    es: 'Sitio ',
    it: 'Sito ',
    de: 'Website ',
    nl: 'Website ',
  },
  companyPageTitle: {
    id: `${companyScope}.companyPageTitle`,
    defaultMessage:
      'Tous vos {transportType} <span class="highlighted">au même endroit.</span>',
    en: 'All your {transportType} <span class="highlighted">in one place.</span>',
    es: 'Todos tus {transportType} <span class="highlighted">en un solo lugar.</span>',
    it: 'Tutti i tuoi {transportType} <span class="highlighted">in un unico posto.</span>',
    de: 'Alle deine {transportType} <span class="highlighted">an einem Ort.</span>',
    nl: 'Al uw {transportType} <span class="highlighted">op één plek.</span>',
  },
  companyPageSubtitle: {
    id: `${companyScope}.companyPageSubtitle`,
    defaultMessage: 'Réservez vos billets de {transportType} parmi :',
    en: 'Book your {transportType} tickets among:',
    es: 'Reserve sus billetes de {transportType} entre:',
    it: 'Prenota i tuoi biglietti {transportType} tra:',
    de: 'Buchen Sie Ihre {transportType}-Tickets unter:',
    nl: 'Boek uw {transportType}-tickets tussen:',
  },
  [`plural-${ETransportType.BUS}`]: {
    id: `${companyScope}.plural-${ETransportType.BUS}`,
    defaultMessage: 'bus',
    en: 'bus',
    es: 'autobús',
    it: 'autobus',
    de: 'Bus',
    nl: 'bus',
  },
  [`plural-${ETransportType.TRAIN}`]: {
    id: `${companyScope}.plural-${ETransportType.TRAIN}`,
    defaultMessage: 'trains',
    en: 'trains',
    es: 'trenes',
    it: 'treni',
    de: 'Züge',
    nl: 'treinen',
  },
  [`plural-${ETransportType.CARPOOLING}`]: {
    id: `${companyScope}.plural-${ETransportType.CARPOOLING}`,
    defaultMessage: 'covoiturages',
    en: 'carpooling',
    es: 'coche compartido',
    it: 'car pooling',
    de: 'Mitfahrgelegenheiten',
    nl: 'carpoolen',
  },
};

export default defineMessages(companyMessages);
